import React, { useState, useEffect, useRef } from "react";
import { Link, Redirect, useLocation } from "react-router-dom";

// Custom scrollbar
import { Scrollbars } from "react-custom-scrollbars";

// Cookies
import { useCookies } from "react-cookie";

// Redyx
import { useDispatch } from "react-redux";
import { globalSlice } from "../../services/globalSlice/globalSlice";

// API
import AuthApi from "../../services/api/auth";

// Stylesheet
import "./Nav.css";

// Components
import {
  LanguageSwitcher,
  LightDarkModeSwitcher,
} from "../FormElements/FormElements";

// Assets
import Logo from "../../assets/images/logo/primary.svg";
import { UnmountClosed } from "react-collapse";
import { useTranslation } from "react-multi-lang";
import ReactTooltip from "react-tooltip";
import ReportConfigurations from "../ReportConfigurations/ReportConfigurations";

interface NavProps {
  list: {
    icon: string;
    name: string;
    sub_name?: string;
    link?: string;
    show: boolean;
    childs?: {
      icon: string;
      name: string;
      link: string;
      show: boolean;
    }[];
  }[];
  active: string | null;
}

// Simple Toast Component
const SimpleToast = ({ message, type, onClose }: { message: string; type: 'success' | 'error'; onClose: () => void }) => {
  React.useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div style={{
      position: 'fixed',
      bottom: '30px',
      right: '30px',
      padding: '12px 20px',
      backgroundColor: type === 'success' ? '#4CAF50' : '#f44336',
      color: 'white',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      zIndex: 10001,
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      fontSize: '14px',
      fontWeight: '500',
      fontFamily: 'inherit',
      animation: 'fadeInUp 0.3s ease-out'
    }}>
      <span>{message}</span>
      <button onClick={onClose} style={{
        background: 'none',
        border: 'none',
        color: 'white',
        cursor: 'pointer',
        fontSize: '16px',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        opacity: 0.8
      }}>✕</button>
    </div>
  );
};

export const SideNav = (props: NavProps) => {
  const getActiveIndexByName = (name: string | null): number => {
    let matched_index = 0;
    props.list.map((item, index) => {
      if (item.link?.substring(1) === name) matched_index = index;
    });
    return matched_index;
  };

  const getActiveBoxPositionByIndex = (index: number): number => {
    return index * 90;
  };

  const [activeBox, setActiveBox] = useState<number>(
    getActiveBoxPositionByIndex(getActiveIndexByName(props.active))
  );

  return (
    <nav className="side-nav">
      <Scrollbars
        autoHeight
        autoHeightMin="75vh"
        autoHide
        renderTrackHorizontal={(props) => (
          <div
            {...props}
            className="track-horizontal"
            style={{ display: "none" }}
          />
        )}
        renderThumbHorizontal={(props) => (
          <div
            {...props}
            className="thumb-horizontal"
            style={{ display: "none" }}
          />
        )}
      >
        <div className="active-box" style={{ top: activeBox }} />
        <ul>
          {props.list.map((item, index) => {
            if (!item.show) return;
            if (item.childs)
              return (
                <>
                  <li
                    key={"side-" + index}
                    className={
                      item.childs
                        .map((item) => item.link.substring(1))
                        .includes(props.active || "")
                        ? "active"
                        : ""
                    }
                    onClick={() =>
                      setActiveBox(getActiveBoxPositionByIndex(index))
                    }
                    data-tip={item.sub_name}
                  >
                    <Link to={item.childs[0]?.link || ""}>
                      <span>
                        <i className={item.icon}></i> {item.name}
                      </span>
                    </Link>
                  </li>
                  <UnmountClosed
                    key={"collapse-" + index}
                    isOpened={item.childs
                      .map((item) => item.link.substring(1))
                      .includes(props.active || "")}
                  >
                    <ul>
                      {item.childs.map((child, child_index) => (
                        <li
                          key={"child-" + index + "-" + child_index}
                          className={
                            child.link.substring(1) === props.active
                              ? "active"
                              : ""
                          }
                        >
                          <Link to={child.link}>
                            <span>
                              <i className={child.icon}></i> {child.name}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </UnmountClosed>
                </>
              );
            else
              return (
                <li
                  key={"side-" + index}
                  className={
                    item.link?.substring(1) === props.active ? "active" : ""
                  }
                  onClick={() =>
                    setActiveBox(getActiveBoxPositionByIndex(index))
                  }
                >
                  <Link to={item.link || ""}>
                    <span>
                      <i className={item.icon}></i> {item.name}
                    </span>
                  </Link>
                </li>
              );
          })}
        </ul>
      </Scrollbars>
      <ReactTooltip place="top" effect="solid" />
    </nav>
  );
};

export const TopNav = () => {
  // React hooks
  const [redirect, setRedirect] = useState<boolean>(false);
  const [showReportConfig, setShowReportConfig] = useState<boolean>(false);
  const [link, setLink] = useState<string>("");
  const [showTo, setShowTo] = useState(false);
  
  // State for ECL trigger
  const [isTriggeringECL, setIsTriggeringECL] = useState<boolean>(false);
  const [showYearQuarterModal, setShowYearQuarterModal] = useState<boolean>(false);
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [selectedQuarter, setSelectedQuarter] = useState<string>("");
  
  // State for toast
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  
  // State for dynamic years and quarters from API
  const [availableYears, setAvailableYears] = useState<string[]>([]);
  const [availableQuarters, setAvailableQuarters] = useState<string[]>([]);
  const [isLoadingYearsQuarters, setIsLoadingYearsQuarters] = useState<boolean>(false);
  
  // State for progress tracking
  const [showProgressModal, setShowProgressModal] = useState<boolean>(false);
  const [progress, setProgress] = useState<{
    total_clients: number;
    dispatched_clients: number;
    completed_clients: number;
    processed_clients: number;
    failed_clients: number;
    failed_list: any[];
    remaining: number;
    percentage: number;
    status: string;
    is_complete: boolean;
  }>({
    total_clients: 0,
    dispatched_clients: 0,
    completed_clients: 0,
    processed_clients: 0,
    failed_clients: 0,
    failed_list: [],
    remaining: 0,
    percentage: 0,
    status: 'idle',
    is_complete: false
  });
  
  const pollingInterval = useRef<NodeJS.Timeout | null>(null);

  // Cookies hooks
  const [cookie, __, removeCookie] = useCookies();

  // Redux
  const dispatch = useDispatch();

  // Translation
  const t = useTranslation();

  // API
  const ENDPOINTS = new AuthApi();

  // Fetch years and quarters when modal opens
  useEffect(() => {
    if (showYearQuarterModal) {
      fetchYearsAndQuarters();
    }
  }, [showYearQuarterModal]);

  // Function to fetch years and quarters from API
  const fetchYearsAndQuarters = async () => {
    setIsLoadingYearsQuarters(true);
    try {
      // const apiUrl = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
      // const apiUrl ='https://mbi2025.zainrohn.com/api/public';
 const apiUrl = 'https://idb.zainrohn.com/api/public';
      
       const response = await fetch(`${apiUrl}/records`, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
      if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
      const result = await response.json();
      
      if (result.success && result.data) {
        setAvailableYears(result.data.years || []);
        setAvailableQuarters(result.data.quarters || []);
        
        if (result.data.years && result.data.years.length > 0) {
          setSelectedYear(result.data.years[0]);
        }
        if (result.data.quarters && result.data.quarters.length > 0) {
          setSelectedQuarter(result.data.quarters[0]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch years and quarters:', error);
    } finally {
      setIsLoadingYearsQuarters(false);
    }
  };

  const logout = () => {
    dispatch(globalSlice.actions.setIsLoading(true));
    ENDPOINTS.auth()
      .logout(null)
      .then((response: any) => {
        if (response.data.message === "success") {
          dispatch(globalSlice.actions.setIsLoading(false));
          removeCookie("userinfo");
          setTimeout(() => {
            setRedirect(true);
          }, 10);
        }
      });
  };

  // Stop polling when component unmounts
  useEffect(() => {
    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, []);

  // Function to fetch status
  const fetchStatus = async () => {
    console.log(' fetchStatus called at:', new Date().toLocaleTimeString());
    try {
      // const apiUrl = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
      // const apiUrl = 'https://mbi2025.zainrohn.com/api/public';
       const apiUrl = 'https://idb.zainrohn.com/api/public';
      const url = `${apiUrl}/ecl-status?year=${selectedYear}&quarter=${selectedQuarter}`;
      
      // const response = await fetch(url);
      const response = await fetch(`${url}`, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
      const data = await response.json();
      
      console.log('📊 Progress update:', {
        dispatched: data.dispatched_clients,
        completed: data.completed_clients,
        failed: data.failed_clients,
        total: data.total_clients,
        percentage: data.percentage,
        status: data.status
      });
      
      setProgress({
        total_clients: data.total_clients || 0,
        dispatched_clients: data.dispatched_clients || data.total_clients || 0,
        completed_clients: data.completed_clients || data.processed_clients || 0,
        processed_clients: data.processed_clients || 0,
        failed_clients: data.failed_clients || 0,
        failed_list: data.failed_list || [],
        remaining: data.remaining || 0,
        percentage: data.percentage || 0,
        status: data.status || 'processing',
        is_complete: data.is_complete || false
      });
      
      if (data.is_complete && pollingInterval.current) {
        console.log('✅ Process complete, stopping polling');
        clearInterval(pollingInterval.current);
        pollingInterval.current = null;
      }
    } catch (error) {
      console.error('💥 Failed to fetch status:', error);
    }
  };

  // Function to show progress modal (when job is already running)
  const showProgress = () => {
    if (progress.total_clients > 0 && !progress.is_complete) {
      setShowProgressModal(true);
    }
  };

  // Function to trigger ECL calculation
  const triggerECLCalculation = async () => {
    console.log('🚀 1. Button clicked - Starting ECL calculation');
    
    // If job is already running and modal is hidden, just show it
    if (progress.total_clients > 0 && !progress.is_complete && !showProgressModal) {
      console.log('📊 Job already running, showing progress modal');
      setShowProgressModal(true);
      return;
    }
    
    // If modal is already showing, don't start a new calculation
    if (showProgressModal) {
      console.log('⚠️ Calculation already in progress');
      return;
    }
    
    // Show modal IMMEDIATELY when button is clicked (before API call)
    setShowProgressModal(true);
    setProgress({
      total_clients: 0,
      dispatched_clients: 0,
      completed_clients: 0,
      processed_clients: 0,
      failed_clients: 0,
      failed_list: [],
      remaining: 0,
      percentage: 0,
      status: 'starting',
      is_complete: false
    });
    
    setIsTriggeringECL(true);
    setShowYearQuarterModal(false);
    
    try {
      // const apiUrl = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
      // const apiUrl = 'https://mbi2025.zainrohn.com/api/public';
      const apiUrl = 'https://idb.zainrohn.com/api/public';
      console.log('🚀 2. Calling API:', `${apiUrl}/trigger-ecl`);
      
      const response = await fetch(`${apiUrl}/trigger-ecl`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify({
          year: selectedYear,
          quarter: selectedQuarter
        })
      });
      
      const result = await response.json();
      console.log('🚀 3. API result:', result);
      
      if (response.ok && result.success) {
        // Success - update progress with real data (modal already showing)
        setToast({ message: `✅ ECL calculation started for ${result.clients_to_process} clients!`, type: 'success' });
        
        setProgress({
          total_clients: result.clients_to_process,
          dispatched_clients: 0,
          completed_clients: 0,
          processed_clients: 0,
          failed_clients: 0,
          failed_list: [],
          remaining: result.clients_to_process,
          percentage: 0,
          status: 'processing',
          is_complete: false
        });
        
        // Start polling every 3 seconds
        if (pollingInterval.current) {
          clearInterval(pollingInterval.current);
        }
        pollingInterval.current = setInterval(fetchStatus, 3000);
        
        // Fetch immediately
        fetchStatus();
      } else {
        // No clients found or other error - close modal and show toast
        setToast({ message: `❌ ${result.message || 'No clients found for the specified period'}`, type: 'error' });
        setShowProgressModal(false);
      }
    } catch (error) {
      console.error('ECL trigger error:', error);
      setToast({ message: `❌ Failed to trigger ECL calculation: ${error}`, type: 'error' });
      setShowProgressModal(false);
    } finally {
      setIsTriggeringECL(false);
    }
  };

  return (
    <>
      {/* Toast Notification */}
      {toast && (
        <SimpleToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      
      <nav className="top-nav">
        <img src={Logo} className="logo" />

        <div className="actions">
          <div className="reports-list">
            <span>{t("reports")}</span>
            <ul>
              <li>
                <a
                  onClick={() => {
                    setShowReportConfig(true);
                    // setLink("http://127.0.0.1:8000/reports/cif");
                    // setLink("http://mbi2025.zainrohn.com/reports/cif");
                    setLink("http://idb.zainrohn.com/reports/cif");
                      
                    setShowTo(false);
                  }}
                >
                  {t("cif")}
                </a>
              </li>
              <li>
                <a
                  onClick={() => {
                    setShowReportConfig(true);
                    // setLink("http://127.0.0.1:8000/reports/ecl-per-account");
                    // setLink("http://mbi2025.zainrohn.com/reports/ecl-per-account");]
                    setLink("http://idb.zainrohn.com/reports/ecl-per-account");
                    setShowTo(false);
                  }}
                >
                  {"Ecl Per Account"}
                </a>
              </li>
              <li>
                <a
                  onClick={() => {
                    setShowReportConfig(true);
                    // setLink("http://127.0.0.1:8000/reports/ecl-per-cif");
                    // setLink("http://mbi2025.zainrohn.com/reports/ecl-per-cif");
                    setLink("http://idb.zainrohn.com/reports/ecl-per-cif");
                    setShowTo(false);
                  }}
                >
                  {"Ecl Per Cif"}
                </a>
              </li>
              <li>
                <a
                  onClick={() => {
                    setShowReportConfig(true);
                    // setLink("http://127.0.0.1:8000/reports/ecl-per-cif-ccy");
                    // setLink("http://mbi2025.zainrohn.com/reports/ecl-per-cif-ccy");
                    setLink("http://idb.zainrohn.com/reports/ecl-per-cif-ccy");
                    setShowTo(false);
                  }}
                >
                  {"Ecl Per Cif Ccy"}
                </a>
              </li>
              <li>
                <a
                  onClick={() => {
                    setShowReportConfig(true);
                    // setLink("http://127.0.0.1:8000/reports/disclosure");
                  
                    // setLink("http://mbi2025.zainrohn.com/reports/disclosure");
                    setLink("http://idb.zainrohn.com/reports/disclosure");
                    setShowTo(false);
                  }}
                >
                  {"Disclosure"}
                </a>
              </li>

              {/* ECL Trigger Button */}
              <li>
                <a
                  onClick={() => setShowYearQuarterModal(true)}
                  style={{ 
                    cursor: isTriggeringECL ? "not-allowed" : "pointer",
                    opacity: isTriggeringECL ? 0.6 : 1,
                    backgroundColor: "#925b97",
                    color: "#fff",
                    padding: "8px 12px",
                    borderRadius: "4px",
                    display: "inline-block"
                  }}
                >
                  <i className="icon-calculator" style={{ marginRight: "5px" }}></i>
                  {isTriggeringECL ? "Processing..." : "Calculate ECL"}
                </a>
              </li>
            </ul>
          </div>

          {/* Show Progress Button - appears when modal is hidden but job is running */}
          {!showProgressModal && progress.total_clients > 0 && !progress.is_complete && (
            <button
              onClick={showProgress}
              style={{
                position: "fixed",
                bottom: "20px",
                right: "20px",
                padding: "10px 15px",
                backgroundColor: "#925b97",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                zIndex: 9999,
                fontSize: "14px",
                fontWeight: "bold",
                boxShadow: "0 2px 5px rgba(0,0,0,0.2)"
              }}
            >
              Show Progress ({progress.percentage}%)
            </button>
          )}

          {/* Year/Quarter Selection Modal */}
          {showYearQuarterModal && (
            <div style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999
            }}>
              <div style={{
                backgroundColor: "#fff",
                borderRadius: "8px",
                padding: "24px",
                minWidth: "320px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
              }}>
                <h3 style={{ margin: "0 0 20px 0", fontSize: "18px", fontWeight: "600" }}>
                  Calculate ECL
                </h3>
                
                <div style={{ marginBottom: "16px" }}>
                  <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "500" }}>
                    Year
                  </label>
                  {isLoadingYearsQuarters ? (
                    <div style={{ padding: "8px", color: "#666" }}>Loading years...</div>
                  ) : (
                    <select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        fontSize: "14px"
                      }}
                    >
                      {availableYears.map((year) => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  )}
                </div>
                
                <div style={{ marginBottom: "24px" }}>
                  <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "500" }}>
                    Quarter
                  </label>
                  {isLoadingYearsQuarters ? (
                    <div style={{ padding: "8px", color: "#666" }}>Loading quarters...</div>
                  ) : (
                    <select
                      value={selectedQuarter}
                      onChange={(e) => setSelectedQuarter(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        fontSize: "14px"
                      }}
                    >
                      {availableQuarters.map((quarter) => (
                        <option key={quarter} value={quarter}>{quarter.toUpperCase()}</option>
                      ))}
                    </select>
                  )}
                </div>
                
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
                  <button
                    onClick={() => setShowYearQuarterModal(false)}
                    style={{
                      padding: "8px 16px",
                      background: "transparent",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px"
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => triggerECLCalculation()}
                    style={{
                      padding: "8px 16px",
                      background: "#925b97",
                      color: "#fff",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px"
                    }}
                    disabled={availableYears.length === 0 || availableQuarters.length === 0}
                  >
                    Calculate
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Progress Modal with Real-time Updates */}
          {showProgressModal && (
            <div style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10000
            }}>
              <div style={{
                backgroundColor: "#fff",
                borderRadius: "8px",
                padding: "24px",
                minWidth: "450px",
                maxWidth: "600px",
                maxHeight: "80vh",
                overflowY: "auto",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                textAlign: "center"
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                  <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "600" }}>
                    {progress.total_clients === 0 ? "Starting ECL Calculation..." : 
                     progress.status === "completed_with_errors" ? "⚠️ ECL Calculation Completed with Errors" :
                     progress.is_complete ? "✅ ECL Calculation Completed" : " ECL Calculation in Progress"}
                  </h3>
                  {/* Hide Button - closes modal but DOES NOT stop the job */}
                  <button
                    onClick={() => {
                      console.log('Hide button clicked - calculation continues in background');
                      setShowProgressModal(false);
                      // DO NOT stop polling - keep fetching status in background
                      // DO NOT clear progress - keep it for when modal reappears
                    }}
                    style={{
                      background: "transparent",
                      border: "none",
                      fontSize: "20px",
                      cursor: "pointer",
                      color: "#999",
                      padding: "0 5px"
                    }}
                    title="Hide progress (calculation continues)"
                  >
                    ✕
                  </button>
                </div>
                
                {/* Progress Bar - Show loading animation when starting */}
                {progress.total_clients === 0 ? (
                  <div style={{
                    width: "100%",
                    backgroundColor: "#f0f0f0",
                    borderRadius: "10px",
                    overflow: "hidden",
                    marginBottom: "15px"
                  }}>
                    <div style={{
                      width: "100%",
                      backgroundColor: "#925b97",
                      height: "35px",
                      animation: "pulse 1.5s ease-in-out infinite",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontSize: "14px",
                      fontWeight: "bold"
                    }}>
                      Initializing...
                    </div>
                  </div>
                ) : (
                  <div style={{
                    width: "100%",
                    backgroundColor: "#f0f0f0",
                    borderRadius: "10px",
                    overflow: "hidden",
                    marginBottom: "15px"
                  }}>
                    <div style={{
                      width: `${progress.percentage}%`,
                      backgroundColor: progress.status === "completed_with_errors" ? "#ff9800" : 
                                       progress.percentage === 100 ? "#4CAF50" : "#925b97",
                      height: "35px",
                      transition: "width 0.5s ease-in-out",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontSize: "14px",
                      fontWeight: "bold"
                    }}>
                      {progress.percentage > 0 && `${progress.percentage}%`}
                    </div>
                  </div>
                )}
                
                {/* Statistics */}
                <div style={{ marginBottom: "20px", fontSize: "14px", color: "#555" }}>
                  {progress.total_clients === 0 ? (
                    <p>Preparing calculation...</p>
                  ) : (
                    <>
                      <p style={{ margin: "5px 0" }}>
                          Dispatched: <strong>{progress.dispatched_clients.toLocaleString()}</strong> / {progress.total_clients.toLocaleString()} clients
                      </p>
                      <p style={{ margin: "5px 0" }}>
                        ✅ Completed: <strong style={{ color: "#4CAF50" }}>{progress.completed_clients.toLocaleString()}</strong> clients
                      </p>
                      {progress.failed_clients > 0 && (
                        <p style={{ margin: "5px 0" }}>
                          ❌ Failed: <strong style={{ color: "#f44336" }}>{progress.failed_clients.toLocaleString()}</strong> clients
                        </p>
                      )}
                      <p style={{ margin: "5px 0" }}>
                         Remaining: <strong>{progress.remaining.toLocaleString()}</strong> clients
                      </p>
                      <p style={{ margin: "5px 0" }}>
                          Progress: <strong>{progress.percentage}%</strong>
                      </p>
                      <p style={{ margin: "5px 0" }}>
                        {progress.is_complete ? (
                          progress.failed_clients > 0 ? (
                            <span style={{ color: "#ff9800" }}>⚠️ Completed with {progress.failed_clients} error(s)</span>
                          ) : (
                            <span style={{ color: "#4CAF50" }}>✅ All done successfully!</span>
                          )
                        ) : (
                          <span style={{ color: "#925b97" }}> Processing... Please wait</span>
                        )}
                      </p>
                    </>
                  )}
                </div>
                
                {/* Error List */}
                {progress.failed_list && progress.failed_list.length > 0 && (
                  <div style={{
                    marginTop: "15px",
                    padding: "10px",
                    backgroundColor: "#ffebee",
                    borderRadius: "4px",
                    textAlign: "left",
                    maxHeight: "200px",
                    overflowY: "auto"
                  }}>
                    <p style={{ margin: "0 0 10px 0", fontWeight: "bold", color: "#f44336" }}>
                      ⚠️ Failed Clients:
                    </p>
                    {progress.failed_list.map((failed, index) => (
                      <div key={index} style={{
                        padding: "5px",
                        borderBottom: "1px solid #ffcdd2",
                        fontSize: "12px"
                      }}>
                        <strong>CIF:</strong> {failed.cif} | 
                        <strong> Name:</strong> {failed.name || 'N/A'} | 
                        <strong> Error:</strong> <span style={{ color: "#f44336" }}>{failed.error}</span>
                      </div>
                    ))}
                    {progress.failed_clients > 20 && (
                      <p style={{ margin: "10px 0 0 0", fontSize: "12px", color: "#666" }}>
                        ... and {progress.failed_clients - 20} more errors
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          <ReportConfigurations
            open={showReportConfig}
            toggle={() => setShowReportConfig(false)}
            link={link}
            showTo={showTo}
          />

          <div className="switchers">
            <LightDarkModeSwitcher />
            <span className="margin-10" />
            <LanguageSwitcher />
          </div>

          <div className="userinfo">
            <span>
              <strong>{cookie?.userinfo?.name}</strong> <br />{" "}
              {cookie?.userinfo?.role?.name}
            </span>
            <i className="icon-username-1" />
          </div>

          <i className="icon-logout" onClick={logout} />
        </div>

        {redirect ? <Redirect to="/" /> : ""}
      </nav>
    </>
  );
};